import uniq from 'lodash/uniq'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import { AMENITY_INCLUSION_RULES } from '@pcln/fly-ui-components'

import { getAirlinesForThisSlice } from '../../Slice/helpers'

export function brandsInSlice(curSlice) {
  const segments = get(curSlice, 'segments')
  return uniq(segments.map((seg) => seg.brandId).filter((brand) => !!brand))
}

function mapBrandsToTier(brandReference) {
  return brandReference.reduce((acc, cur) => {
    acc[cur.brandId] = parseInt(cur.tier, 10)

    return acc
  }, {})
}

function getBrand(brandReference, id) {
  return brandReference.find((b) => b.brandId === id)
}

function lowestTierBrand(brands, brandReference) {
  if (brands.length > 1) {
    let brandId = brands[0].brandId

    brands.reduce((acc, curBrand) => {
      if (mapBrandsToTier(brandReference)[curBrand] < acc) {
        brandId = curBrand
        return mapBrandsToTier(brandReference)[curBrand]
      }

      return acc
    }, Infinity)

    return getBrand(brandReference, brandId)
  }

  return getBrand(brandReference, brands[0])
}

function sliceWithBrandInfo(brand, airline, seatsRemaining) {
  return {
    brandId: brand.brandId,
    brandName: brand.name,
    // price: baseFarePrice, // TODO confirm that base fare tile still has a price
    included: brand.brandAttributes.filter(
      (attr) => attr.inclusion === AMENITY_INCLUSION_RULES.INCLUDED.name
    ),
    notIncluded: brand.brandAttributes.filter(
      (attr) => attr.inclusion !== AMENITY_INCLUSION_RULES.INCLUDED.name
    ),
    seatsRemaining,
    airline,
  }
}

function hasAvailableAttributes(brand) {
  return get(brand, 'brandAttributes', []).length > 0
}

function airlineName(slice, itinerary) {
  const airlines = getAirlinesForThisSlice(get(slice, 'segments', []), itinerary.airlines)
  return airlines.length === 1 ? airlines[0].name : 'Multiple Airlines'
}

function dropDuplicateAttributeSlices(attributesBySlice) {
  // WARNING: This only works with OW, RT, and Split
  if (attributesBySlice.length === 2) {
    const slice1 = omit(attributesBySlice[0], ['displayPriority', 'availabilityText'])
    const slice2 = omit(attributesBySlice[1], ['displayPriority', 'availabilityText'])

    if (isEqual(slice1.included, slice2.included) && isEqual(slice1.notIncluded, slice2.notIncluded)) {
      return [slice1]
    }
  }

  return attributesBySlice
}

function containsBrand(slices, brand) {
  return brand && slices.find((slice) => slice.brandId === brand.brandId)
}

/**
 * Returns array of brand reference objects that contain the attributes to
 * display for each slice of an itinerary. If all slices have the same attributes
 * and inclusion levels, only the first slice will be returned.
 * @param itinerary
 * @param brandReference
 * @param baseFarePrice
 * @returns {*}
 */
export default function getBrandAttributesBySlice(itinerary, brandReference) {
  if (!isArray(brandReference)) return []

  const attributesBySlice = get(itinerary, 'slices', []).reduce((attrSlices, curSlice) => {
    const brand = lowestTierBrand(brandsInSlice(curSlice), brandReference)

    if (!containsBrand(attrSlices, brand) && hasAvailableAttributes(brand)) {
      attrSlices.push(sliceWithBrandInfo(brand, airlineName(curSlice, itinerary), itinerary.seatsAvailable))
    }

    return attrSlices
  }, [])

  return dropDuplicateAttributeSlices(attributesBySlice)
}
